/* ============================================================================
 * Linktop 6-in-1 Health Device — Web Bridge
 * ----------------------------------------------------------------------------
 * The physical device speaks a proprietary BLE protocol and computes its vital
 * values inside native (compiled C/C++) algorithm libraries that ship with the
 * vendor's Android SDK. A browser cannot execute those libraries, so the real
 * device is reached through a thin **Android WebView shell** that hosts the
 * native SDK and injects a JavaScript interface as `window.LinktopNative`.
 *
 * This module abstracts that boundary:
 *   • If `window.LinktopNative` is present  -> drive the real native SDK.
 *   • Otherwise (any normal browser)        -> run a built-in SIMULATOR so the
 *     whole Scan → Pair → Measure → Save flow is fully demoable & QA-testable.
 *
 * The native shell must implement the contract documented in
 * /app/docs/linktop_gateway_bridge_contract.md
 * ==========================================================================*/

const NATIVE = () =>
  (typeof window !== "undefined" && window.LinktopNative) || null;

export function bridgeMode() {
  return NATIVE() ? "native" : "simulator";
}
export function isNativeBridge() {
  return !!NATIVE();
}

/* ---- simulator helpers ---------------------------------------------------*/
const rnd = (a, b, decimals = 0) => {
  const x = a + Math.random() * (b - a);
  return decimals ? +x.toFixed(decimals) : Math.round(x);
};

function simulatedResult(type) {
  switch (type) {
    case "bp":
      return { bp_systolic: rnd(108, 138), bp_diastolic: rnd(68, 88), heart_rate: rnd(60, 88) };
    case "spo2":
      return { spo2: rnd(95, 99), heart_rate: rnd(60, 90) };
    case "temperature":
      return { temperature: rnd(36.2, 37.4, 1) };
    case "ecg":
      return { heart_rate: rnd(58, 92), hrv: rnd(35, 75), rhythm: "Normal sinus rhythm" };
    case "glucose":
      return { blood_glucose: rnd(4.4, 7.6, 1) };
    case "cholesterol":
      return { cholesterol: rnd(3.4, 5.6, 1) };
    case "uric_acid":
      return { uric_acid: rnd(200, 400) };
    default:
      return {};
  }
}

// Duration (ms) each measurement takes in the simulator.
const SIM_DURATION = {
  bp: 5000, spo2: 4500, temperature: 3000, ecg: 6000,
  glucose: 5000, cholesterol: 5000, uric_acid: 5000,
};
const WAVE_TYPES = new Set(["spo2", "ecg"]);

/* ============================================================================
 * Public API
 * ==========================================================================*/

/**
 * Scan for nearby devices.
 * @param {(device:{id:string,name:string,rssi:number})=>void} onDevice
 * @returns {()=>void} stop function
 */
export function scanForDevices(onDevice) {
  const native = NATIVE();
  if (native) {
    const handler = (e) => onDevice(e.detail);
    window.addEventListener("linktop:device", handler);
    try { native.scan(); } catch (_) {}
    return () => {
      window.removeEventListener("linktop:device", handler);
      try { native.stopScan && native.stopScan(); } catch (_) {}
    };
  }
  // Simulator — surface a couple of fake devices on a short delay.
  const timers = [
    setTimeout(() => onDevice({ id: "LT-6IN1-SIM-01", name: "Linktop 6-in-1 (Sim)", rssi: -52 }), 700),
    setTimeout(() => onDevice({ id: "LT-6IN1-SIM-02", name: "Linktop 6-in-1 (Sim)", rssi: -71 }), 1600),
  ];
  return () => timers.forEach(clearTimeout);
}

/**
 * Connect to a device by id/address.
 * @returns {Promise<{deviceId:string,name:string,battery:number,firmware:string}>}
 */
export function connectDevice(deviceId, name) {
  const native = NATIVE();
  if (native) {
    return new Promise((resolve, reject) => {
      const ok = (e) => { cleanup(); resolve(e.detail); };
      const err = (e) => { cleanup(); reject(new Error(e.detail?.message || "Connection failed")); };
      const cleanup = () => {
        window.removeEventListener("linktop:connected", ok);
        window.removeEventListener("linktop:error", err);
      };
      window.addEventListener("linktop:connected", ok);
      window.addEventListener("linktop:error", err);
      try { native.connect(deviceId); } catch (e) { cleanup(); reject(e); }
    });
  }
  return new Promise((resolve) =>
    setTimeout(
      () => resolve({ deviceId, name: name || "Linktop 6-in-1 (Sim)", battery: rnd(45, 100), firmware: "v2.7.1" }),
      1200,
    ),
  );
}

export function disconnectDevice() {
  const native = NATIVE();
  if (native) { try { native.disconnect(); } catch (_) {} }
}

/**
 * Start a single-vital measurement.
 * @param {string} type  bp | spo2 | temperature | ecg | glucose | cholesterol | uric_acid
 * @param {{onStatus?:Function,onWave?:Function,onResult?:Function,onError?:Function}} handlers
 * @returns {()=>void} cancel function
 */
export function startMeasure(type, { onStatus, onWave, onResult, onError } = {}) {
  const native = NATIVE();
  if (native) {
    const status = (e) => e.detail?.type === type && onStatus?.(e.detail.stage, e.detail.message);
    const wave = (e) => e.detail?.type === type && onWave?.(e.detail.points);
    const result = (e) => { if (e.detail?.type === type) { cleanup(); onResult?.(e.detail.values); } };
    const error = (e) => { if (!e.detail?.type || e.detail.type === type) { cleanup(); onError?.(e.detail?.message || "Measurement failed"); } };
    const cleanup = () => {
      window.removeEventListener("linktop:status", status);
      window.removeEventListener("linktop:wave", wave);
      window.removeEventListener("linktop:result", result);
      window.removeEventListener("linktop:error", error);
    };
    window.addEventListener("linktop:status", status);
    window.addEventListener("linktop:wave", wave);
    window.addEventListener("linktop:result", result);
    window.addEventListener("linktop:error", error);
    try { native.startMeasure(type); } catch (e) { cleanup(); onError?.(String(e)); }
    return () => { cleanup(); try { native.stopMeasure && native.stopMeasure(); } catch (_) {} };
  }

  /* ---- simulator ---- */
  let cancelled = false;
  const duration = SIM_DURATION[type] || 4000;
  onStatus?.("starting");
  const startT = setTimeout(() => {
    if (cancelled) return;
    onStatus?.("measuring");
  }, 600);

  let waveTimer = null;
  if (WAVE_TYPES.has(type)) {
    let t = 0;
    waveTimer = setInterval(() => {
      if (cancelled) return;
      // ECG: spiky QRS-like; SpO2: smooth PPG sine.
      const pts = Array.from({ length: 48 }, (_, i) => {
        const phase = (t + i) * 0.35;
        if (type === "ecg") {
          const beat = Math.sin(phase) * 0.2 + (Math.sin(phase * 3) > 0.95 ? 1 : 0) * (Math.random() * 0.3 + 0.7);
          return beat + (Math.random() - 0.5) * 0.05;
        }
        return Math.sin(phase) * 0.5 + 0.5 + (Math.random() - 0.5) * 0.04;
      });
      onWave?.(pts);
      t += 4;
    }, 120);
  }

  const doneT = setTimeout(() => {
    if (cancelled) return;
    if (waveTimer) clearInterval(waveTimer);
    onResult?.(simulatedResult(type));
  }, duration);

  return () => {
    cancelled = true;
    clearTimeout(startT);
    clearTimeout(doneT);
    if (waveTimer) clearInterval(waveTimer);
  };
}
